import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageComments from '../components/PageComments';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { buildProblemSourceDraft, problemDraftStorageKey } from '../data/problems';
import { listVisiblePageEntries } from '../services/contentService';
import { getPublicProfileByUsername } from '../services/profileService';

function PublicPages() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function loadPages() {
      setLoading(true);
      setError('');
      try {
        const loadedProfile = await getPublicProfileByUsername(username);
        const loadedEntries = loadedProfile ? await listVisiblePageEntries(loadedProfile.ownerId, currentUser?.uid || null) : [];
        if (active) {
          setProfile(loadedProfile);
          setEntries(loadedEntries);
        }
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadPages();
    return () => { active = false; };
  }, [currentUser, username]);

  if (loading) return <div className="surface-card">Loading pages...</div>;
  if (error) return <p className="error-text">{error}</p>;
  if (!profile) return <section className="auth-panel"><SEO title="Pages Not Found" /><h1>Pages not found</h1></section>;

  function createProblemFromEntry(entry) {
    localStorage.setItem(problemDraftStorageKey, JSON.stringify(buildProblemSourceDraft({
      sourceType: 'pageEntry',
      sourceId: entry.id,
      sourceTitle: entry.prompt,
      sourceText: entry.answer,
    })));
    navigate('/dashboard/problems');
  }

  return (
    <>
      <SEO title={`${profile.displayName} Pages`} />
      <section className="page-heading">
        <p className="eyebrow">Pages</p>
        <h1>{profile.displayName}&apos;s pages</h1>
        <p>Visible prompt answers from this autobiography.</p>
      </section>
      <section className="surface-card">
        <div className="page-tile-grid">
          {entries.map((entry) => (
            <article className={`page-tile page-tile-${entry.tileFormat || 'square'}`} key={entry.id}>
              <div className="tile-action-row">
                <p className="tag">{entry.theme} · {entry.entryDate}</p>
                <button className="tile-arrow-button" type="button" title="Open a problem from this tile" onClick={() => createProblemFromEntry(entry)}>-&gt;</button>
              </div>
              <h3>{entry.prompt}</h3>
              {entry.imageUrl ? <img src={entry.imageUrl} alt="Page attachment" /> : null}
              <p>{entry.answer}</p>
              {entry.visibility !== 'private' ? <PageComments entry={entry} canComment={entry.visibility !== 'private'} /> : null}
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export default PublicPages;
